import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";

import {
  UserOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  GiftOutlined,
  CompassOutlined,
} from "@ant-design/icons";

export default function MasterDataManagement() {

  const navigate =
    useNavigate();

  const [counts, setCounts] =
    useState({

      users: 0,

      teams: 0,

      destinations: 0,

      // 🔥 Offers intelligence
      offersActive: 0,
      offersExpired: 0,
      offersPaused: 0,
      offersExhausted: 0,
      offersExpiringSoon: 0,
      offersFeatured: 0,
      offersHidden: 0,
    });

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {

    const fetchCounts =
      async () => {

        try {

          setLoading(true);

          const [

            usersRes,

            teamsRes,

            destRes,

            offersRes,

          ] = await Promise.all([

            apiClient.get(
              "/users"
            ),

            apiClient.get(
              "/teams"
            ),

            apiClient.get(
              "/destinations"
            ),

            apiClient.get(
              "/offers"
            ),
          ]);

          /* =========================================
             SAFE RESPONSE NORMALIZATION
          ========================================= */

          const users =

            usersRes.data?.data ||

            usersRes.data?.users ||

            [];

          const teams =

            teamsRes.data?.data ||

            teamsRes.data?.teams ||

            [];

          const destinations =

            destRes.data?.data ||

            destRes.data?.destinations ||

            [];

          const offers =

            offersRes.data?.data ||

            offersRes.data?.offers ||

            [];

          const now =
            new Date();

          /* =========================================
             OFFER STATES
          ========================================= */

          const activeOffers =
            offers.filter(
              (o) =>

                o.status ===
                  "active" &&

                new Date(
                  o.validUntil
                ) > now &&

                (
                  !o.maxUsageCount ||

                  o.currentUsageCount <
                    o.maxUsageCount
                )
            );

          const expiredOffers =
            offers.filter(
              (o) =>

                new Date(
                  o.validUntil
                ) <= now
            );

          const pausedOffers =
            offers.filter(
              (o) =>
                o.status ===
                "paused"
            );

          const exhaustedOffers =
            offers.filter(
              (o) =>

                o.maxUsageCount &&

                o.currentUsageCount >=
                  o.maxUsageCount
            );

          /* =========================================
             ADVANCED OFFER INTELLIGENCE
          ========================================= */

          const soonThreshold =
            new Date();

          soonThreshold.setDate(
            soonThreshold.getDate() + 3
          );

          const expiringSoonOffers =
            offers.filter(
              (o) =>

                new Date(
                  o.validUntil
                ) > now &&

                new Date(
                  o.validUntil
                ) <= soonThreshold
            );

          const featuredOffers =
            offers.filter(
              (o) =>
                o.isFeatured ===
                true
            );

          const hiddenOffers =
            offers.filter(
              (o) =>
                o.isVisible ===
                false
            );

          /* =========================================
             UPDATE COUNTS
          ========================================= */

          setCounts({

            users:
              users.length,

            teams:
              teams.length,

            destinations:
              destinations.length,

            offersActive:
              activeOffers.length,

            offersExpired:
              expiredOffers.length,

            offersPaused:
              pausedOffers.length,

            offersExhausted:
              exhaustedOffers.length,

            offersExpiringSoon:
              expiringSoonOffers.length,

            offersFeatured:
              featuredOffers.length,

            offersHidden:
              hiddenOffers.length,
          });

        } catch (err) {

          console.error(
            "Admin stats error:",
            err
          );

        } finally {

          setLoading(false);
        }
      };

    fetchCounts();

  }, []);

  const modules = [

    {
      key: "users",

      title: "Users",

      description:
        "Manage users, roles & permissions",

      icon:
        <UserOutlined />,

      route:
        "/users",

      color:
        "bg-blue-500",
    },

    {
      key: "teams",

      title: "Teams",

      description:
        "Team hierarchy & assignments",

      icon:
        <TeamOutlined />,

      route:
        "/teams",

      color:
        "bg-purple-500",
    },

    {
      key: "destinations",

      title:
        "Destinations",

      description:
        "Cities, states & countries",

      icon:
        <EnvironmentOutlined />,

      route:
        "/destinations",

      color:
        "bg-green-500",
    },

    {
      key: "offers",

      title:
        "Offers",

      description:
        "Reusable travel offers",

      icon:
        <GiftOutlined />,

      route:
        "/offers",

      color:
        "bg-orange-500",
    },

    {
      key: "activities",

      title:
        "Activities Master",

      description:
        "Sightseeing & Bulk Catalog",

      icon:
        <CompassOutlined />,

      route:
        "/activities",

      color:
        "bg-blue-600",
    },
  ];

  return (

    <div className="p-6">

      {/* HEADER */}

      <div className="mb-6">

        <h2 className="text-2xl font-semibold">

          Admin Control Center

        </h2>

        <p className="text-gray-500 text-sm">

          Manage all master data for your CRM

        </p>

      </div>

      {/* LOADING */}

      {loading && (

        <div className="text-gray-400 text-sm mb-4">

          Loading dashboard data...

        </div>
      )}

      {/* GRID */}

      <div
        className="
          grid
          grid-cols-1
          sm:grid-cols-2
          md:grid-cols-3
          lg:grid-cols-4
          gap-6
        "
      >

        {modules.map((mod) => (

          <div

            key={mod.key}

            onClick={() =>
              navigate(
                mod.route
              )
            }

            className="
              cursor-pointer
              rounded-2xl
              bg-white
              shadow-sm
              hover:shadow-xl
              transition-all
              duration-300
              transform
              hover:-translate-y-2
              p-5
              border
              border-gray-100
            "
          >

            {/* ICON */}

            <div
              className={`
                w-14
                h-14
                flex
                items-center
                justify-center
                rounded-full
                text-white
                text-xl
                ${mod.color}
              `}
            >

              {mod.icon}

            </div>

            {/* TITLE */}

            <h3 className="mt-4 text-lg font-semibold">

              {mod.title}

            </h3>

            {/* DESCRIPTION */}

            <p className="text-sm text-gray-500 mt-1">

              {mod.description}

            </p>

            {/* COUNTS */}

            <div className="mt-3 text-xs font-medium text-gray-400">

              {mod.key === "offers"

                ? loading

                  ? "..."

                  : (

                    <>

                      {/* PRIMARY */}

                      {counts.offersActive} active •{" "}

                      {counts.offersExpired} expired

                      {/* SECONDARY */}

                      <div className="text-[10px] text-gray-300 mt-1">

                        {counts.offersPaused} paused •{" "}

                        {counts.offersExhausted} exhausted

                      </div>

                      {/* ALERT */}

                      {counts.offersExpiringSoon > 0 && (

                        <div className="text-[10px] text-yellow-500 mt-1">

                          ⚠ {counts.offersExpiringSoon} expiring soon

                        </div>
                      )}

                      {/* FEATURED */}

                      {counts.offersFeatured > 0 && (

                        <div className="text-[10px] text-blue-400 mt-1">

                          ⭐ {counts.offersFeatured} featured

                        </div>
                      )}

                      {/* HIDDEN */}

                      {counts.offersHidden > 0 && (

                        <div className="text-[10px] text-red-400 mt-1">

                          🚫 {counts.offersHidden} hidden

                        </div>
                      )}

                    </>
                  )

                : loading

                  ? "..."

                  : `${counts[mod.key]} items`
              }

            </div>

          </div>
        ))}

      </div>

    </div>
  );
}
